/**
 * Fidora Voice Service - OpenAI TTS Integration
 * Replaces browser TTS with professional OpenAI voices
 */

// Resolve voice backend per environment
const PROD_BASE = 'https://us-central1-fidora-72b3d.cloudfunctions.net';

function isProdHost() {
  try {
    const h = window.location.host || '';
    return h.endsWith('web.app') || h.endsWith('firebaseapp.com');
  } catch { return false; }
}

function getLocalBase() {
  return process.env.REACT_APP_VOICE_SERVER || 'http://localhost:8000';
}

function getTTSUrl() {
  return isProdHost() ? `${PROD_BASE}/fidoraTTS` : `${getLocalBase()}/api/tts`;
}

// Map AI coach names to voice IDs (human-first names)
const COACH_VOICE_MAP = {
  // Current AI coaches
  // Note: Atlas uses 'sage' voice ID, Sage uses 'george' voice ID
  'george': 'george',  // Sage uses george voice ID
  'sage': 'sage',      // Atlas uses sage voice ID
  'marcus': 'marcus',
  'nova': 'nova',
  'sarah': 'sarah',
  'maya': 'maya',
  // Legacy mappings (for backward compatibility)
  'George': 'george',
  'Sage': 'george',    // Sage uses george voice ID
  'Marcus': 'marcus',
  'Nova': 'nova',
  'Sarah': 'sarah',
  'Maya': 'maya',
  // Creative names → voice IDs
  'atlas': 'sage',     // Atlas uses sage voice ID
  'Atlas': 'sage',     // Atlas uses sage voice ID
  'phoenix': 'marcus',
  'Phoenix': 'marcus',
  'aurora': 'sarah',
  'Aurora': 'sarah',
  'catalyst': 'maya',
  'Catalyst': 'maya',
  // Other legacy mappings
  'Roger': 'sage',
  'Bill': 'marcus',
  'Jessica': 'nova',
  'Rachel': 'sarah',
  'Amelia': 'maya',
  'Charlie': 'marcus',
  'Laura': 'sarah',
  'Harry': 'sage',
  'Gracie': 'nova'
};

/**
 * Get all available voices
 */
export async function getVoices() {
  // Always return static voices - no need to call server
  return [
    { id: 'nova', name: 'Nova', description: 'Caring guide - Deeply empathetic, gentle, patient listener', gender: 'female', style: 'empathetic' },
    { id: 'george', name: 'George', description: 'Steady friend - Mature, grounded, reliable support', gender: 'male', style: 'warm' },
    { id: 'sage', name: 'Sage', description: 'Deep wisdom - Grounded, experienced, thoughtful mentor', gender: 'male', style: 'wise' },
    { id: 'marcus', name: 'Marcus', description: 'Decisive clarity - Confident, direct, professional guidance', gender: 'male', style: 'strategic' },
    { id: 'sarah', name: 'Sarah', description: 'Polished grace - Sophisticated, articulate, professional', gender: 'female', style: 'professional' },
    { id: 'maya', name: 'Maya', description: 'Authentic confidence - Warm, genuine, inspiring presence', gender: 'female', style: 'confident' }
  ];
}

/**
 * Get voice preview URL
 */
export function getVoicePreviewUrl(voiceId) {
  const mappedVoice = COACH_VOICE_MAP[voiceId] || voiceId;
  // Preview route only available locally; in production we will synthesize via TTS directly
  return `${getLocalBase()}/api/preview/${mappedVoice}`;
}

/**
 * Generate speech from text using OpenAI TTS via Firebase
 * @param {string} text - Text to convert to speech
 * @param {string} voiceName - Voice name/ID
 * @returns {Promise<Blob>} Audio blob
 */
export async function textToSpeech(text, voiceName = 'george') {
  try {
    // Map legacy voice names
    const voiceId = COACH_VOICE_MAP[voiceName] || voiceName;
    
    // In production, use the agent.js service which properly calls Firebase
    if (isProdHost()) {
      // Use the Firebase callable function via agent.js
      const { generateSpeech } = await import('./agent.js');
      return await generateSpeech(text, voiceId);
    }
    
    // Local development fallback
    const response = await fetch(getTTSUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        voice: voiceId,
        model: 'tts-1-hd' // High definition model
      }),
    });

    if (!response.ok) {
      throw new Error(`TTS failed: ${response.statusText}`);
    }

    return await response.blob();
  } catch (error) {
    console.error('Error generating speech:', error);
    throw error;
  }
}

/**
 * INTERNAL: keep a single managed audio element to avoid overlaps
 */
let managedAudio = null;
let managedUrl = null;

function cleanupManagedAudio() {
  if (managedAudio) {
    try { managedAudio.pause(); } catch {}
  }
  if (managedUrl) {
    try { URL.revokeObjectURL(managedUrl); } catch {}
  }
  managedAudio = null;
  managedUrl = null;
}

/**
 * Play audio from text with single-instance management
 * - Stops any currently playing audio
 * - Returns the Audio element for UI control
 */
export async function playManaged(text, voiceName) {
  try {
    // Stop and clean any existing audio first
    cleanupManagedAudio();

    const audioBlob = await textToSpeech(text, voiceName);
    managedUrl = URL.createObjectURL(audioBlob);
    managedAudio = new Audio(managedUrl);

    managedAudio.onended = () => cleanupManagedAudio();
    managedAudio.onerror = () => cleanupManagedAudio();
    await managedAudio.play();
    return managedAudio;
  } catch (error) {
    cleanupManagedAudio();
    console.error('Error playing audio:', error);
    throw error;
  }
}

export function pauseManaged() {
  if (managedAudio && !managedAudio.paused) {
    managedAudio.pause();
  }
}

export function resumeManaged() {
  if (managedAudio && managedAudio.paused) {
    managedAudio.play();
  }
}

export function stopManaged() {
  cleanupManagedAudio();
}

export function getManagedAudio() {
  return managedAudio;
}

/**
 * Check if voice server is available
 */
export async function isVoiceServerAvailable() {
  try {
    if (isProdHost()) {
      // Assume Cloud Function is available; a real call happens in textToSpeech
      return true;
    }
    const response = await fetch(`${getLocalBase()}/health`, { timeout: 2000 });
    return response.ok;
  } catch {
    return false;
  }
}

export default {
  getVoices,
  getVoicePreviewUrl,
  textToSpeech,
  playManaged,
  pauseManaged,
  resumeManaged,
  stopManaged,
  getManagedAudio,
  isVoiceServerAvailable
};

